import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { Chip, Divider, Grid, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material';

import { Button } from '../../../../../common/button';
import { getAllHubsState } from '../../../../../../features/pop/get_all_hubs';
import { getAllPopsState } from '../../../../../../features/pop/get_all_pops';
import { getReservationStatusColor } from '../../../../../common/utils/reservation_utils';

const ReservationDialog = ({ reservation }) => {
    const [open, setOpen] = useState(false);

    const getAllHubs = useSelector(getAllHubsState);
    const getAllPops = useSelector(getAllPopsState);

    const onClose = () => setOpen(false);

    return (
        <>
            <Tooltip title="Szczegóły rezerwacji">
                <IconButton size="small" onClick={ () => setOpen(true) }>
                    <AssignmentIcon fontSize="small"/>
                </IconButton>
            </Tooltip>

            <Dialog open={ open } onClose={ onClose } fullWidth maxWidth="md">
                <DialogTitle>
                    <Grid container alignItems="center" justifyContent="space-between">
                        <Grid>
                            <Typography variant="h6" fontWeight="bold">
                                Rezerwacja { reservation?.reservationId }
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                Data rezerwacji: <b>{ reservation?.date }</b>
                            </Typography>
                        </Grid>
                        <Chip
                            size="medium"
                            label={ reservation?.displayStatusValue }
                            color={ getReservationStatusColor(reservation?.status) }
                            sx={ { fontWeight: 600 } }
                        />
                    </Grid>
                </DialogTitle>
                <Divider/>

                <DialogContent sx={ { paddingTop: 2 } }>
                    <Grid container spacing={ 3 }>
                        <Grid item xs={ 12 }>
                            <Typography variant="subtitle2" color="textSecondary"
                                        sx={ { textTransform: 'uppercase', fontWeight: 600, mb: 1 } }>
                                Dane klienta
                            </Typography>
                            <Grid container spacing={ 1 }>
                                <Grid item xs={ 12 } sm={ 6 }>
                                    <Typography variant="body2">Imię i nazwisko:</Typography>
                                    <Typography variant="body1" fontWeight={ 500 }>
                                        { `${ reservation?.customer?.name || 'Brak' } ${ reservation?.customer?.surname || 'Brak' }` }
                                    </Typography>
                                </Grid>
                                <Grid item xs={ 12 } sm={ 6 }>
                                    <Typography variant="body2">Telefon:</Typography>
                                    <Typography variant="body1" fontWeight={ 500 }>
                                        { `${ reservation?.customer?.phonePrefix || '' } ${ reservation?.customer?.phoneNumber || '' }` }
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item xs={ 12 }><Divider/></Grid>

                        <Grid item xs={ 12 }>
                            <Typography variant="subtitle2" color="textSecondary"
                                        sx={ { textTransform: 'uppercase', fontWeight: 600, mb: 1 } }>
                                Szczegóły rezerwacji
                            </Typography>
                            <Grid container spacing={ 1 }>
                                <Grid item xs={ 12 } sm={ 4 }>
                                    <Typography variant="body2">Numer zamówienia:</Typography>
                                    <Typography variant="body1"
                                                fontWeight={ 500 }>{ reservation?.reservationOrderId }</Typography>
                                </Grid>
                                <Grid item xs={ 12 } sm={ 4 }>
                                    <Typography variant="body2">Hub:</Typography>
                                    <Typography variant="body1" fontWeight={ 500 }>
                                        { getAllHubs?.data?.find((hub) => hub?.id === reservation?.hubId)?.name || '-' }
                                    </Typography>
                                </Grid>
                                <Grid item xs={ 12 } sm={ 4 }>
                                    <Typography variant="body2">Punkt odbioru:</Typography>
                                    <Typography variant="body1" fontWeight={ 500 }>
                                        { getAllPops?.data?.find((pop) => pop?.id === reservation?.popId)?.name || '-' }
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid item xs={ 12 }><Divider/></Grid>

                        <Grid item xs={ 12 }>
                            <Typography variant="subtitle2" color="textSecondary"
                                        sx={ { textTransform: 'uppercase', fontWeight: 600, mb: 2 } }>
                                Zarezerwowane produkty
                            </Typography>
                            <TableContainer component={ Paper } variant="outlined">
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow sx={ { '& th': { backgroundColor: 'slate.50', fontWeight: 600 } } }>
                                            <TableCell>Nazwa produktu</TableCell>
                                            <TableCell align="center">Liczba szt.</TableCell>
                                            <TableCell align="center">Półka</TableCell>
                                            <TableCell align="center">Produkt zwrócony</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        { reservation?.items?.map((item, index) => (
                                            <TableRow key={ index } hover>
                                                <TableCell
                                                    sx={ { fontWeight: 500 } }>{ item?.product?.productName }</TableCell>
                                                <TableCell align="center">{ item?.quantity }</TableCell>
                                                <TableCell align="center">{ item?.segmentName }</TableCell>
                                                <TableCell align="center">
                                                    <Chip
                                                        label={ item?.returned ? "TAK" : "NIE" }
                                                        color={ item?.returned ? "warning" : "success" }
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                </TableCell>
                                            </TableRow>
                                        )) }
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions sx={ { p: 2, borderTop: theme => `1px solid ${ theme.palette.divider }` } }>
                    <Button onClick={ onClose } variant="outlined">Zamknij</Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default ReservationDialog;